import { UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Image as ImageIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { useGetTeamBalance } from "../../../api/generated/endpoints/billing/billing";
import { getGetJobQueryKey } from "../../../api/generated/endpoints/generation/generation";
import type { Catalog } from "../../../api/generated/model";
import { PromptBox } from "../../../platform/studio/composer/PromptBox";
import { ComposerErrors, PriceAndStart } from "../../../platform/studio/composer/Start";
import { useComposer } from "../../../platform/studio/composer/use-composer";
import { SourceThumb } from "../../../platform/studio/image/SourceThumb";
import { shortestOf } from "../../../platform/studio/video/shots";
import { formatCount } from "../../../ui/format";
import { ProductStep } from "./ProductStep";
import { SimpleResult } from "./SimpleResult";
import { Suggestions } from "./Suggestions";
import classes from "./simple.module.css";

const NO_SHAPE = () => undefined;
/** Live's words on a video frame's library dialog (the video composer's). */
const FRAME_DIALOG = { title: "Select Frame", subtitle: "Choose an image for this frame" };

interface DescribeProps {
  teamId: string;
  brandId: string;
  catalog: Catalog;
  medium: "image" | "video";
}

/**
 * Steps 2 and 3 for an image or a video, on the Studio's own composer: the catalog's default
 * model and the type's defaults, but one output and, for a video, the shortest length (a real
 * run is the cheapest; spec ui-perf/simple-studio, decision 2). No control changes them. Then the
 * started job's result in place of nothing.
 */
export function Describe({ teamId, brandId, catalog, medium }: DescribeProps) {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const c = useComposer({
    catalog,
    medium,
    initialSource: null,
    initialType: undefined,
    clone: undefined,
    onStarted: (job) => {
      // The result reads the job the start answered at once; the stream keeps it current.
      queryClient.setQueryData(getGetJobQueryKey(teamId, job.id), job);
      setJobId(job.id);
    },
    onShapeChange: NO_SHAPE,
  });
  const { set } = c;
  const outputs = c.draft.outputs;
  const length = c.draft.durationSeconds;
  const shortest = shortestOf(c.model, c.type.id);
  useEffect(() => {
    if (outputs !== 1) set({ outputs: 1 });
    if (shortest !== null && length !== shortest) set({ durationSeconds: shortest });
  }, [outputs, length, shortest, set]);
  const balance = useGetTeamBalance(teamId).data;
  const full = c.references.length >= c.referencesRoom;
  return (
    <>
      <ProductStep
        teamId={teamId}
        brandId={brandId}
        chosen={productId}
        onChoose={(product) => {
          // "No product" leaves a video's own start picture alone.
          if (product === null && productId === null) return;
          setProductId(product?.productId ?? null);
          c.chooseProduct(product);
        }}
      />
      <section className={classes.step} aria-labelledby="simple-describe">
        <h2 id="simple-describe" className={classes.stepTitle}>
          Describe it
        </h2>
        <div className={classes.card}>
          <div className={classes.prompt}>
            <PromptBox c={c} />
          </div>
          <Suggestions
            teamId={teamId}
            brandId={brandId}
            onApply={(prompt, images, templateId) =>
              medium === "image" ? c.applyTemplate(prompt, images, templateId) : c.set({ prompt })
            }
          />
          {medium === "image" && c.canSource ? (
            <div className={classes.row}>
              <UnstyledButton className={classes.chip} onClick={c.openReferences} disabled={full}>
                <ImageIcon size={14} aria-hidden /> Add a reference image
              </UnstyledButton>
              {c.references.map((each) => (
                <SourceThumb
                  key={each.assetId}
                  view={each}
                  removeLabel={`Remove ${each.name}`}
                  onRemove={() => c.removeReference(each.assetId)}
                />
              ))}
            </div>
          ) : null}
          {medium === "video" && productId === null ? (
            // A video starts from text, or from a picture: the product's photo, else one chosen.
            <div className={classes.row}>
              {c.view === null ? (
                <UnstyledButton className={classes.chip} onClick={() => c.openLibrary(FRAME_DIALOG, "start")}>
                  <ImageIcon size={14} aria-hidden /> Start from a picture
                </UnstyledButton>
              ) : (
                <SourceThumb view={c.view} removeLabel={`Remove ${c.view.name}`} onRemove={c.removeSource} />
              )}
            </div>
          ) : null}
          <div className={classes.gobar}>
            {balance === undefined ? null : (
              <span className={classes.balance}>Balance: {formatCount(balance.available)} credits</span>
            )}
            <span className={classes.go}>
              <PriceAndStart c={c} />
            </span>
          </div>
          <ComposerErrors c={c} />
        </div>
        {c.dialog}
      </section>
      {jobId === null ? null : (
        <SimpleResult
          teamId={teamId}
          brandId={brandId}
          jobId={jobId}
          onAgain={c.startJob}
          onAnother={() => setJobId(null)}
        />
      )}
    </>
  );
}
